// What the slide editor has in hand in this browser (S5): what is selected on the open slide (boxes by id, a layout's
// fields as "f:title"), which one is being typed in, where the last click was (the strip of slides or the slide, so
// the keys know which they act on), what was copied, and the colours used lately. Nothing here is saved to the board.

import { create } from "zustand";
import type { FieldName, SlideBox, SlideColor, FieldPlace } from "../../../../shared/slideDesign";
import { readStoredJson, writeStoredJson } from "../../../lib/stored";

/** A thing on the slide that can be selected: a box by its id, or a layout's field. */
export type Sel = string;
export const fieldKey = (f: FieldName): Sel => `f:${f}`;
export const fieldOf = (k: Sel): FieldName | null => (k.startsWith("f:") ? (k.slice(2) as FieldName) : null);

export interface Clip {
  boxes: SlideBox[];
  /** the words the copy put on the system clipboard, to know a paste of them as this copy */
  text: string;
  /** fields copied are pasted as text boxes with their words */
  from: string;
  at: number;
}

interface DesignUi {
  /** the slide the selection is on */
  slide: string | null;
  sel: Sel[];
  /** the box or field being typed in */
  typing: Sel | null;
  zone: "rail" | "slide";
  /** a box just inserted waits for its words: left empty, it goes */
  fresh: string | null;
  /** the next widget the gallery adds goes on this slide as a box */
  widgetTo: string | null;
  /** while a box is moved, resized or turned: how it looks now, written once it is let go */
  preview: { boxes: Record<string, Partial<SlideBox>>; place: Partial<Record<FieldName, FieldPlace>> } | null;
  guides: { x: number[]; y: number[] } | null;
  recent: SlideColor[];
}

const RECENT = "cb:slides:recent-colors";

function loadRecent(): SlideColor[] {
  const r = readStoredJson<unknown>(RECENT, []);
  return Array.isArray(r) ? (r.filter((c) => typeof c === "string").slice(0, 8) as SlideColor[]) : [];
}

export const useDesign = create<DesignUi>(() => ({ slide: null, sel: [], typing: null, zone: "slide", fresh: null, widgetTo: null, preview: null, guides: null, recent: loadRecent() }));

/** What was copied, in this tab (the system clipboard carries it to other tabs and decks too). */
export let clip: Clip | null = null;
export function setClip(c: Clip | null) {
  clip = c;
}

export function select(slide: string, sel: Sel[], typing: Sel | null = null) {
  useDesign.setState({ slide, sel, typing, zone: "slide" });
}

export function clearSelection() {
  useDesign.setState({ sel: [], typing: null });
}

export function useSel(slideId: string | null): Sel[] {
  return useDesign((s) => (s.slide === slideId ? s.sel : EMPTY));
}
const EMPTY: Sel[] = [];

/** A colour used, first among the recent ones. */
export function usedColor(c: SlideColor | null | undefined) {
  if (!c) return;
  const recent = [c, ...useDesign.getState().recent.filter((x) => x !== c)].slice(0, 8);
  useDesign.setState({ recent });
  writeStoredJson(RECENT, recent); // kept for this visit only when storage is out of reach
}
